// PlacementPage.jsx
import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  Truck,
  Cpu,
  Heart,
  Briefcase,
  Users,
  ArrowRight,
  Zap,
  ChevronDown,
  Users as UsersIcon,
} from "lucide-react";
import Layout from "../component/layout/Layout";

/*
  Requirements:
  1) npm install lucide-react
  2) Import and render <PlacementPage /> where needed
*/

export default function PlacementPage() {
  const [openAccordion, setOpenAccordion] = useState(null);

  const handleAccordionToggle = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  useEffect(() => {
    // Simple scroll reveal using plain JS
    const revealEls = document.querySelectorAll(".reveal-on-scroll");
    function reveal() {
      const windowH = window.innerHeight;
      revealEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < windowH - 100) {
          el.classList.add("visible");
        }
      });
    }
    reveal();
    window.addEventListener("scroll", reveal);
    return () => {
      window.removeEventListener("scroll", reveal);
    };
  }, []);

  // CTA click
  function handleCTA() {
    window.open(`/contact-us`);
  }

  const industries = [
    { icon: Truck, label: "Automotive & EV" },
    { icon: Briefcase, label: "Manufacturing & Engineering" },
    { icon: Cpu, label: "Electronics & Electrical" },
    { icon: Heart, label: "Healthcare & Services" },
    { icon: Users, label: "Sales & Operations" },
    { icon: Cpu, label: "IT, Support & Digital Roles" },
  ];

  const steps = [
    { index: 1, title: "Skill Readiness", desc: "Training aligned to real job roles — not theory." },
    { index: 2, title: "Career Profiling", desc: "Your strengths, preferences and future goals mapped." },
    { index: 3, title: "Opportunity Access", desc: "Employers don’t just see profiles. They see capability." },
    { index: 4, title: "Interview Confidence", desc: "We prepare you to represent your skills with clarity." },
    { index: 5, title: "Placement Momentum", desc: "Support continues until opportunity meets preparation." },
  ];

  const accordionItems = [
    {
      title: "What is JOBSERVE?",
      content: "JOBSERVE is our placement marketplace where verified roles, employer briefs and structured assessments are used to match learner capability with job requirements. Candidates are presented with role-specific evidence of skill — not just a resume.",
    },
    {
      title: "How does placement support work?",
      content: "We provide end-to-end placement support: role mapping, interview preparation, employer introductions, and continued follow-up until the right match is secured.",
    },
    {
      title: "Ready to stop hoping?",
      content: "Start positioning. Opportunities don’t happen — they’re built, one skill at a time.",
    },
  ];

  return (
    <Layout>
      <div className="ppage-root">
        <style>{styles}</style>

        {/* HERO */}
        <header className="pp-hero">
          <div className="container">
            <div className="pp-hero__inner">
              <div className="pp-hero__content">
                <h1 className="pp-title">
                  Placement Support
                </h1>
                <p className="pp-lead">
                  <strong>We Don’t Train for Certificates. We Train for Jobs.</strong>
                </p>
                <p className="pp-sub">
                  Skills Without Jobs Are Incomplete. Anyone can offer courses. Few can offer direction.
                  At SkillServe, placement is not an extra service — it is the result of solid training.
                  We don’t push learners into the market unprepared. We prepare learners for the market.
                </p>

                <div className="pp-cta-row">
                  <button className="pp-cta" onClick={handleCTA}>
                    <span>Start Your Career on JOBSERVE</span>
                    <ArrowRight size={18} strokeWidth={2.5} />
                  </button>
                  <button
                    className="pp-cta pp-cta--ghost"
                    onClick={() => alert("Redirecting to JOBSERVE (demo)...")}
                  >
                    Apply for Jobs via JOBSERVE
                  </button>
                </div>
              </div>

              <div className="pp-hero__visual reveal-on-scroll">
                <div className="pp-hero-card">
                  <div className="pp-hero-card__icon">
                    <Zap size={32} />
                  </div>
                  <h3>From Classroom to Career</h3>
                  <ul className="pp-checks">
                    <li><CheckCircle size={16} /> Skill-building with purpose</li>
                    <li><CheckCircle size={16} /> Career grooming with intent</li>
                    <li><CheckCircle size={16} /> Job opportunities with relevance</li>
                    <li><CheckCircle size={16} /> Placement support with accountability</li>
                  </ul>
                  <p className="muted">Your journey does not end with training. It starts there.</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* MAIN CONTENT */}
        <main className="pp-main">
          <section className="pp-section reveal-on-scroll">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">How We Build Employability</h2>
                <p className="section-sub">
                  We don’t match resumes. We match skills to roles.
                </p>
              </div>

              <div className="steps-grid">
                {steps.map((step) => (
                  <div key={step.index} className="step-card">
                    <div className="step-index">{step.index}</div>
                    <div className="step-body">
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="tagline">You’re exactly where you need to be.</p>
            </div>
          </section>

          <section className="pp-section industries-section reveal-on-scroll">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Opportunities Across Industries</h2>
              </div>
              <div className="industries-grid">
                {industries.map((industry, idx) => {
                  const Icon = industry.icon;
                  return (
                    <div key={idx} className="industry-card">
                      <Icon size={24} />
                      <span>{industry.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="pp-section comparison-section reveal-on-scroll">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Why SkillServe Placement is Different</h2>
              </div>

              <div className="comparison-grid">
                <div className="comparison-column bad">
                  <h4>Typical Approaches</h4>
                  <ul>
                    <li>❌ Random job lists</li>
                    <li>❌ Fake promises</li>
                    <li>❌ Placement pressure tactics</li>
                  </ul>
                </div>
                <div className="comparison-column good">
                  <h4>Our Approach</h4>
                  <ul>
                    <li>✅ Career guidance</li>
                    <li>✅ Industry exposure</li>
                    <li>✅ Skill alignment</li>
                    <li>✅ Long-term thinking</li>
                  </ul>
                </div>
              </div>

              <p className="muted">We respect careers too much to gamble with them.</p>
            </div>
          </section>

          <section className="pp-section jobsere-section reveal-on-scroll">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">
                  The Power Tool Behind Placement: <span className="accent">JOBSERVE</span>
                </h2>
                <p className="section-sub">
                  JOBSERVE is where employers find ready capability and learners find the right roles — it's the engine that
                  converts training into placements.
                </p>
              </div>

              <div className="accordion-container">
                {accordionItems.map((item, idx) => (
                  <div key={idx} className="accordion-item">
                    <button
                      className={`accordion-btn ${openAccordion === idx ? "open" : ""}`}
                      onClick={() => handleAccordionToggle(idx)}
                    >
                      <span>{item.title}</span>
                      <ChevronDown size={20} className={`accordion-icon ${openAccordion === idx ? "rotate" : ""}`} />
                    </button>
                    <div
                      className={`accordion-panel ${openAccordion === idx ? "open" : ""}`}
                    >
                      <p>{item.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="pp-section cta-section reveal-on-scroll">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Ready to Stop Hoping?</h2>
                <p className="section-sub">Start Positioning. Opportunities don’t happen. They’re built — one skill at a time.</p>
              </div>
              <div className="cta-wrap">
                <button className="pp-cta big" onClick={handleCTA}>
                  <span>Go to JOBSERVE – Find Your Job</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>
    </Layout>
  );
}

/* ---------------------------
   Enhanced CSS for Professional & Aesthetic Design
----------------------------*/
const styles = `
:root {
  --bg-primary: #f8fafc;
  --bg-secondary: #ffffff;
  --accent-primary: #3b82f6;
  --accent-secondary: #f59e0b;
  --text-primary: #1e293b;
  --text-secondary: #64748b;
  --border-color: #e2e8f0;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --container-max: 1200px;
  --spacing-xs: 0.5rem;
  --spacing-sm: 1rem;
  --spacing-md: 1.5rem;
  --spacing-lg: 2rem;
  --spacing-xl: 3rem;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
  line-height: 1.6;
  color: var(--text-primary);
}

.ppage-root {
  background: var(--bg-primary);
  min-height: 100vh;
}

.container {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 var(--spacing-sm);
}

/* HERO */
.pp-hero {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: var(--spacing-xl) 0;
  position: relative;
  overflow: hidden;
}

.pp-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.05;
}

.pp-hero__inner {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: var(--spacing-lg);
  align-items: start;
}

.pp-hero__content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.pp-title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.1;
}

.pp-lead {
  font-size: 1.25rem;
  margin: var(--spacing-xs) 0 var(--spacing-sm) 0;
  font-weight: 600;
}

.pp-sub {
  font-size: 1.1rem;
  opacity: 0.95;
  max-width: 600px;
}

.pp-cta-row {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  margin-top: var(--spacing-md);
}

.pp-cta {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: white;
  color: var(--accent-primary);
  border: none;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  box-shadow: var(--shadow-lg);
  transition: all 0.3s ease;
  text-decoration: none;
}

.pp-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04);
}

.pp-cta--ghost {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: calc(var(--spacing-sm) - 2px) calc(var(--spacing-md) - 2px);
}

.pp-hero__visual {
  position: sticky;
  top: 2rem;
  align-self: start;
}

.pp-hero-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: var(--spacing-lg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.pp-hero-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-md);
}

.pp-hero-card h3 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.pp-checks {
  list-style: none;
  padding: 0;
  margin: var(--spacing-sm) 0 0 0;
}

.pp-checks li {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-xs);
  font-size: 0.95rem;
}

/* MAIN SECTIONS */
.pp-main {
  padding: var(--spacing-xl) 0;
}

.pp-section {
  padding: var(--spacing-lg) 0;
  margin-bottom: var(--spacing-xl);
}

.section-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.section-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  margin: 0 0 var(--spacing-sm) 0;
  letter-spacing: -0.01em;
}

.section-sub {
  font-size: 1.125rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
}

/* STEPS */
.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-md);
}

.step-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  display: flex;
  gap: var(--spacing-md);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
}

.step-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.step-index {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.step-body h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.step-body p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.tagline {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: var(--spacing-lg);
}

/* INDUSTRIES */
.industries-section {
  background: var(--bg-secondary);
}

.industries-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
}

.industry-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  transition: all 0.3s ease;
  cursor: default;
}

.industry-card:hover {
  background: white;
  box-shadow: var(--shadow-md);
}

/* COMPARISON */
.comparison-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin-top: var(--spacing-md);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.comparison-column {
  padding: var(--spacing-lg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.comparison-column h4 {
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.125rem;
  font-weight: 700;
  text-align: center;
}

.comparison-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comparison-column li {
  padding: var(--spacing-xs) 0;
  font-size: 1rem;
}

.bad {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.good {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

/* ACCORDION */
.accordion-container {
  max-width: 700px;
  margin: 0 auto;
  margin-top: var(--spacing-lg);
}

.accordion-item {
  border-bottom: 1px solid var(--border-color);
  overflow: hidden;
}

.accordion-item:last-child {
  border-bottom: none;
}

.accordion-btn {
  width: 100%;
  background: var(--bg-secondary);
  border: none;
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.accordion-btn:hover {
  background: #f8fafc;
}

.accordion-btn.open {
  background: #eff6ff;
}

.accordion-icon {
  transition: transform 0.3s ease;
}

.accordion-icon.rotate {
  transform: rotate(180deg);
}

.accordion-panel {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: var(--bg-secondary);
}

.accordion-panel.open {
  max-height: 200px; /* Adjust based on content */
  padding: 0 var(--spacing-lg) var(--spacing-md) var(--spacing-lg);
}

.accordion-panel p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 1rem;
}

/* CTA SECTION */
.cta-section {
  background: linear-gradient(135deg, var(--accent-primary) 0%, #1d4ed8 100%);
  color: white;
  text-align: center;
  padding: var(--spacing-xl) 0;
}

.cta-section .section-title,
.cta-section .section-sub {
  color: white;
}

.cta-section .section-sub {
  opacity: 0.95;
}

.cta-wrap {
  margin-top: var(--spacing-lg);
}

.pp-cta.big {
  font-size: 1.125rem;
  padding: var(--spacing-md) var(--spacing-xl);
}

/* SCROLL REVEAL */
.reveal-on-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal-on-scroll.visible {
  opacity: 1;
  transform: translateY(0);
}

/* UTILITIES */
.accent {
  color: var(--accent-secondary);
  font-weight: 700;
}

.muted {
  color: var(--text-secondary);
  opacity: 0.8;
  text-align: center;
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .pp-hero__inner {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }

  .pp-hero__visual {
    position: static;
    order: -1;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 var(--spacing-xs);
  }

  .pp-cta-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pp-cta {
    justify-content: center;
  }

  .industries-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .step-card {
    flex-direction: column;
    text-align: center;
  }

  .step-index {
    align-self: center;
  }

  .accordion-panel.open {
    max-height: 300px;
    padding: 0 var(--spacing-md);
  }

  .accordion-btn {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

@media (max-width: 480px) {
  .industries-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .pp-hero {
    padding: var(--spacing-lg) 0;
  }

  .pp-main {
    padding: var(--spacing-lg) 0;
  }
}
`;